import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { emitSessionExpired } from '@/api/authEvents'
import { rawClient } from '@/api/rawClient'
import { apiUrl } from '@/config/env'
import type { TokenResponse } from '@/features/auth/types'
import { clearSession, loadSession, saveSession } from '@/utils/tokenStorage'

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

export const apiClient = axios.create({
  baseURL: apiUrl,
  timeout: 20_000,
})

apiClient.interceptors.request.use((config) => {
  const session = loadSession()
  if (session?.accessToken) {
    config.headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  return config
})

let refreshPromise: Promise<string> | null = null

/** Rota el refresh token una sola vez, aunque varias peticiones fallen en paralelo. */
async function refreshAccessToken(): Promise<string> {
  const session = loadSession()
  if (!session?.refreshToken) {
    throw new Error('No hay sesión activa para refrescar.')
  }

  refreshPromise ??= rawClient
    .post<TokenResponse>('/auth/refresh', { refresh_token: session.refreshToken })
    .then(({ data }) => {
      saveSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + data.expires_in * 1000,
      })
      return data.access_token
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequestConfig | undefined
    const isAuthEndpoint = original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh')

    if (error.response?.status !== 401 || !original || original._retry || isAuthEndpoint) {
      return Promise.reject(error)
    }

    original._retry = true
    try {
      const accessToken = await refreshAccessToken()
      original.headers.set('Authorization', `Bearer ${accessToken}`)
      return apiClient(original)
    } catch (refreshError) {
      clearSession()
      emitSessionExpired()
      return Promise.reject(refreshError)
    }
  },
)
